import { useQuery } from "@tanstack/react-query";
import type { z } from "zod/v4";
import { cameraSearchEndpoint } from "../queries";
import type { DirApiConfig, DirQueryKey } from "../query";
import type { API } from "../../client-api/schema";

export type CameraSearchResult = z.output<typeof API.cameras.search.Response>;

export interface UseCameraSearchOptions extends DirApiConfig {
  assetId: string | undefined;
  enabled?: boolean;
}

/**
 * Search for a camera by asset ID across all realms and tenants.
 *
 * Returns `{ result: { realm, tenant, site, asset, cameras } }`
 * when cameras exist and the user has `camera:view` access, or `{ result: null }` otherwise.
 */
export function useCameraSearch(options: UseCameraSearchOptions) {
  const { assetId, enabled = true, authenticatedFetch, directoryBaseUrl } = options;

  const queryKey: readonly [DirQueryKey & { assetId: string | undefined }] = [
    { feature: "dir", resource: cameraSearchEndpoint.resource, assetId },
  ];

  return useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      if (!assetId) throw new Error("assetId is required");
      const url = `${directoryBaseUrl}${cameraSearchEndpoint.path}?assetId=${encodeURIComponent(assetId)}`;
      const response = await authenticatedFetch(url, { signal });
      if (!response.ok) {
        throw new Error(`Camera search failed: ${String(response.status)}`);
      }
      const data: unknown = await response.json();
      return cameraSearchEndpoint.responseSchema.parse(data);
    },
    enabled: enabled && !!assetId,
    staleTime: 30 * 1000,
    gcTime: 60 * 1000,
  });
}
